import React, { memo, useState } from "react";
import styles from "./SingleReportsDetails.module.scss";
import { combineClasses } from "utils/utils";
import Loader from "components/shared/loader/Loader";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import { ReactComponent as Replace } from "assets/svg/replace.svg"
import { ReactComponent as DeleteIcon } from "assets/svg/CommonDeleteIcon.svg";
import { privatePost, privatePut } from "services/privateRequest";
import { FORM_RESPONSE, UPLOAD } from "services/apiEndpoints";
import { fromJS } from "immutable";
import { useParams } from "react-router-dom";
import { Progress } from "antd";

const ReportsTable = ({ data, setData, loader, share = false }) => {

    const { id } = useParams()
    const [fieldLoading, setFieldLoading] = useState({ loading: false, percent: 0, isError: false, fieldId: -1, mediaIndex: -1 })

    const handleMediaReplace = async (e, index, fieldId, sectionId, parentId) => {
        setFieldLoading((prevState) => ({ ...prevState, loading: true, percent: 0, fieldId: parentId || fieldId, mediaIndex: index }));

        const file = e.target.files[0];
        if (!file) return;
        const baseURL = process.env.REACT_APP_API_BASE_URL;

        const formData = new FormData();
        formData.append("file", file);
        try {
            const response = await privatePost(UPLOAD, formData, {
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const percent = Math.round((progressEvent.loaded * 50) / progressEvent.total); // Scale to 50%
                        setFieldLoading((prevState) => ({ ...prevState, percent }));
                    }
                }
            });
            if (!response?.data?.success) {
                setFieldLoading((prevState) => ({ ...prevState, isError: true })); // Set loading to false and percent to 100% when done
                return;
            }

            const fileData = response?.data?.data[0]
            handleMediaAction(index, fieldId, sectionId, parentId, false, `${baseURL}/uploads/${fileData.filename}`)
        } catch (error) {
            setFieldLoading((prevState) => ({ ...prevState, isError: true })); // Set loading to false and percent to 100% when done
        }
    }

    const handleMediaAction = async (index, fieldId, sectionId, parentId, isDelete, url) => {
        setFieldLoading((prevState) => ({ ...prevState, loading: true, percent: isDelete ? 0 : 50, fieldId: parentId || fieldId, mediaIndex: index }));
        let updatedData = fromJS(data);

        const updateMediaResponse = (mediaResponse) => {
            if (!mediaResponse) return [];
            return isDelete
                ? mediaResponse.delete(index)
                : mediaResponse.set(index, url);
        };

        if (sectionId) {
            const sectionIndex = updatedData.get('sections').findIndex(sec => sec.get('_id') === sectionId);
            if (sectionIndex !== -1) {
                const fieldIndex = updatedData.getIn(['sections', sectionIndex, 'fields']).findIndex(f =>
                    parentId ? f.get('fieldId') === parentId : f.get('fieldId') === fieldId
                );

                if (fieldIndex !== -1) {
                    if (parentId) {
                        const depFieldIndex = updatedData.getIn(['sections', sectionIndex, 'fields', fieldIndex, 'dependentField'])
                            .findIndex(dep => dep.get('fieldId') === fieldId);

                        if (depFieldIndex !== -1) {
                            updatedData = updatedData.updateIn(
                                ['sections', sectionIndex, 'fields', fieldIndex, 'dependentField', depFieldIndex, 'mediaResponse'],
                                updateMediaResponse
                            );
                        }
                    } else {
                        updatedData = updatedData.updateIn(
                            ['sections', sectionIndex, 'fields', fieldIndex, 'mediaResponse'],
                            updateMediaResponse
                        );
                    }
                }
            }
        } else {
            const fieldIndex = updatedData.get('fields').findIndex(f =>
                parentId ? f.get('fieldId') === parentId : f.get('fieldId') === fieldId
            );

            if (fieldIndex !== -1) {
                if (parentId) {
                    const depFieldIndex = updatedData.getIn(['fields', fieldIndex, 'dependentField'])
                        .findIndex(dep => dep.get('fieldId') === fieldId);

                    if (depFieldIndex !== -1) {
                        updatedData = updatedData.updateIn(
                            ['fields', fieldIndex, 'dependentField', depFieldIndex, 'mediaResponse'],
                            updateMediaResponse
                        );
                    }
                } else {
                    updatedData = updatedData.updateIn(
                        ['fields', fieldIndex, 'mediaResponse'],
                        updateMediaResponse
                    );
                }
            }
        }

        try {
            const response = await privatePut(FORM_RESPONSE, id, updatedData,
                {
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.total) {
                            let percent;

                            if (isDelete) {
                                // If deleting, progress should go from 0 to 100
                                percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            } else {
                                // For normal upload, progress goes from 50 to 100
                                const progress = Math.round((progressEvent.loaded * 50) / progressEvent.total); // Scale to 50%
                                percent = 50 + progress;
                            }

                            // Ensure it doesn't jump instantly
                            if (percent > 100) percent = 100;

                            setFieldLoading((prevState) => ({ ...prevState, percent }));
                        }
                    }
                });
            if (!response?.data?.success) {
                console.log('updatedData', updatedData)
                return;
            }
            else {
                setFieldLoading((prevState) => ({ ...prevState, loading: false, percent: 100 }));
            }

            setData(updatedData.toJS()); // Update state with the new data
        } catch (error) {
            console.error("Failed to update form response:", error);
        }
    };

    const renderValueType = (value, index, fieldId, sectionId, parentId) => {
        let mediaExtensions = [
            "jpg", "jpeg", "png", "gif", "bmp", "tiff", "tif", "webp", 
            "svg", "ico", "heic", "heif", "jfif", "avif"
        ];
        
        let videoExtensions = [
            "mp4", "webm", "ogg", "mkv", "mov", "avi", "wmv", "flv", 
            "m4v", "3gp", "3g2"
        ];
        let documentExtensions = {
            pdf: <PictureAsPdfIcon style={{ color: "red" }} />,
            doc: <DescriptionIcon style={{ color: "blue" }} />,
            docx: <DescriptionIcon style={{ color: "blue" }} />,
            xls: <TableChartIcon style={{ color: "green" }} />,
            xlsx: <TableChartIcon style={{ color: "green" }} />,
            ppt: <SlideshowIcon style={{ color: "orange" }} />,
            pptx: <SlideshowIcon style={{ color: "orange" }} />
        };

        let mediaUrl = value?.toString()?.split(".");
        let extension = mediaUrl[mediaUrl.length - 1]?.toLowerCase();

        if (mediaExtensions.includes(extension)) {
            return (
                <>
                    {
                        fieldLoading.loading && fieldLoading.fieldId === (parentId || fieldId) && fieldLoading.mediaIndex === index ?
                            <div style={{ borderRadius: "10px", width: "200px", display: "flex", justifyContent: "center", alignItems: "center", height: "300px", background: "#f2f2f2" }}>
                                <Progress size={50} type="circle" percent={fieldLoading?.percent} status={fieldLoading?.isError && "exception"} />
                            </div>
                            :
                            <div className={styles.imageWrapper}>
                                <div className={styles.imageActions}>
                                    <label htmFor="relace-img" className={styles.iconWrapper} onChange={(e) => { handleMediaReplace(e, index, fieldId, sectionId, parentId) }} >
                                        <input type="file" hidden id="relace-img" />
                                        <Replace />
                                    </label>
                                    <div className={styles.iconWrapper}>
                                        <DeleteIcon
                                            onClick={() => {
                                                handleMediaAction(index, fieldId, sectionId, parentId, true)
                                            }} />
                                    </div>
                                </div>
                                <img
                                    src={value}
                                    alt="Media"
                                    onClick={() => window.open(value, "_blank")}
                                />
                            </div>
                    }
                </>
            );
        } else if (videoExtensions.includes(extension)) {
            return (
                <div style={{ borderRadius: "10px", width: "200px" }}>
                    <video width="100%" height="100%" controls style={{ borderRadius: "10px" }}>
                        <source src={value} type='video/mp4' />
                        Your browser does not support the video tag.
                    </video>
                </div>
            );
        } else if (documentExtensions[extension]) {
            return (
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    {documentExtensions[extension]}
                    <a href={value} target="_blank" rel="noopener noreferrer" style={{ fontWeight: "bold", textDecoration: "none", color: "#007bff" }}>
                        Open Document
                    </a>
                </div>
            );
        } else {
            return share ? <span style={{ fontWeight: "bold", display: "inline-block", width: "100%", height: "100%", padding: "10px", boxSizing: "border-box", background: value == "Yes" ? "green" : value == "No" ? "red" : value == "N/A" ? "blue" : "", color: value == "Yes" | value == "No" || value == "N/A" ? "white" : "" }}>{value}</span> : <span style={{ fontWeight: "bold" }}>{value}</span>

        }
    };


    const renderValue = (arr, isValue) => {
        return arr?.map((val, index) => (
            <p key={index} className={isValue ? styles?.content : ""}>{renderValueType(val)}</p>
        ));
    };

    /** 🔹 Function to render fields & dependent fields */
    const renderFields = (fields, isSection = false, sectionId, parentId) => {
        return fields?.map((field, index) => (
            <React.Fragment key={field.fieldId}>
                <div className={combineClasses(styles?.tableContent, index % 2 !== 0 && true ? styles?.shareBack : styles?.tableBack)}>
                    <p className={styles?.content}>{field?.label}</p>
                    {renderValue(field?.value, true)}
                </div>
                {field?.mediaResponse?.length
                    ? <div className={combineClasses(styles?.imageContainer, index % 2 !== 0 && true ? styles?.shareBack : styles?.tableBack)}>
                        {
                            field?.mediaResponse?.map((val, index) => (
                                renderValueType(val, index, field?.fieldId, sectionId, parentId)
                            ))
                        }
                    </div>
                    : null
                }
                {/* Render Dependent Fields */}
                {field?.dependentField?.length > 0 && renderFields(field?.dependentField, isSection, sectionId, field?.fieldId)}
            </React.Fragment>
        ));
    };

    return (
        <div className={styles?.mainContainer}>
            {loader ? (
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <Loader />
                </div>
            ) : (
                <div className={styles?.tableContainer}>
                    {/* Render Fields (Including Dependent Fields) */}
                    {data?.fields?.length > 0 && renderFields(data?.fields)}

                    {/* Render Sections (Including Their Fields & Dependent Fields) */}
                    {data?.sections?.length > 0 &&
                        data?.sections?.map((section) => (
                            <React.Fragment key={section?.sectionId}>
                                <h3 className={styles?.header}>{section?.name}</h3>
                                {renderFields(section?.fields, true, section._id)}
                            </React.Fragment>
                        ))}
                </div>
            )}
        </div>
    );
};

export default memo(ReportsTable);
